/* eslint-disable import/no-unused-modules */
/* eslint-disable react/prop-types */
import * as React from 'react'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'
import {
  type DayButton,
  DayPicker,
  getDefaultClassNames,
} from 'react-day-picker'

import { cn } from 'components/lib/utils'
import { Button, buttonVariants } from 'components/ui/button'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  // Inline nav keeps the tab order Previous, month, year, Next — the same
  // order as on screen. The default layout places both arrows before the
  // dropdowns in the DOM.
  navLayout = 'around',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-surface-bright p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      navLayout={navLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months
        ),
        // With navLayout="around" the nav buttons are siblings of the caption:
        // [prev] [caption] [next] on one row, the grid spanning all three below.
        month: cn(
          'grid w-full grid-cols-[auto_1fr_auto] items-center gap-y-4',
          defaultClassNames.month
        ),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-[--cell-size] w-full items-center justify-center',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-label-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'relative rounded-md border border-outline-medium focus-within:outline focus-within:outline-2 focus-within:outline-focus-ring',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none',
          captionLayout === 'label'
            ? 'text-label-medium'
            : '[&>svg]:text-on-surface-var flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-label-medium [&>svg]:size-3.5',
          defaultClassNames.caption_label
        ),
        // react-day-picker v9 names the table `month_grid`; the shadcn wrapper
        // shipped the v8 key `table`, which v9 ignores.
        month_grid: cn(
          'col-span-3 w-full border-collapse',
          defaultClassNames.month_grid
        ),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 select-none rounded-md text-label-small uppercase text-on-surface-var',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn(
          'w-[--cell-size] select-none',
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          'select-none text-body-medium text-on-surface-var',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day
        ),
        range_start: cn(
          'rounded-l-md bg-surface',
          defaultClassNames.range_start
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-surface', defaultClassNames.range_end),
        today: cn(
          'rounded-lg outline outline-1 -outline-offset-1 outline-on-surface-var',
          defaultClassNames.today
        ),
        outside: cn(
          'text-on-surface-var aria-selected:text-on-surface-var',
          defaultClassNames.outside
        ),
        disabled: cn('text-on-surface-disabled', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        footer: cn(
          'px-1 pt-2 text-body-small text-on-surface-var',
          defaultClassNames.footer
        ),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon className={cn('size-4', className)} {...props} />
            )
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-4', className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon className={cn('size-4', className)} {...props} />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  // react-day-picker includes ", selected" in aria-label but the parent <td>
  // already has aria-selected="true", causing screen readers to say "selected" twice.
  if (typeof props['aria-label'] === 'string') {
    props = {
      ...props,
      'aria-label': props['aria-label'].replace(/,\s*selected/, ''),
    }
  }
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 text-label-medium leading-none data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-end=true]:bg-core-primary data-[range-middle=true]:bg-surface data-[range-start=true]:bg-core-primary data-[selected-single=true]:bg-core-primary data-[range-end=true]:text-core-on-primary data-[range-middle=true]:text-on-surface data-[range-start=true]:text-core-on-primary data-[selected-single=true]:text-core-on-primary group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 forced-colors:data-[selected-single=true]:outline forced-colors:data-[selected-single=true]:outline-2 [&>span]:text-body-small [&>span]:opacity-70',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
